import React from 'react';
import { isAfterDeliverUntil, isBeforeDeliverFrom } from 'utils/date';
import { DeliveryDetails } from '../../../fetch/reportFetch';
import { Order } from '../../../types/orderTypes';
import { nrOfOrdersByDate } from './nrOfOrdersByDate';
import './ReportUtils.scss';
import { roundToTwo } from './roundToTwo';

export function calcAvgDeliveryRating(deliveries: DeliveryDetails[]): number {
  const deliveriesWithRatingList: number[] = deliveries
    .filter(delivery => delivery.rating !== 0)
    .map(delivery => delivery.rating);

  const sumRatings: number = deliveriesWithRatingList.reduce(
    (sum, rating) => sum + rating,
    0
  );

  const AvgRating: number = sumRatings / deliveriesWithRatingList.length;

  return AvgRating ? AvgRating : 0;
}

export function AvgDeliveryRating({
  deliveries,
}: {
  deliveries: DeliveryDetails[];
}): JSX.Element {
  const AvgRating = calcAvgDeliveryRating(deliveries);

  return (
    <div className="resultsPresentation">
      <div className="resultTitle">Avg Rating</div>
      <p>{roundToTwo(AvgRating)}</p>
    </div>
  );
}

export function calcRevenueNew(orders: Order[]): number {
  const ordersWithNewPriceList = orders
    .filter(order => order.price !== undefined)
    .filter(order => order.price.fractionalDenomination !== 0)
    .map(order => order.price.fractionalDenomination);

  const DivideByHundredFromFractionalDenominationToGetNok = 100;

  const sumRevenueNew = ordersWithNewPriceList.reduce(
    (sum, price) =>
      sum + price / DivideByHundredFromFractionalDenominationToGetNok,
    0
  );

  return sumRevenueNew;
}

export function calcRevenueOld(orders: Order[], defaultPriceNok = 149): number {
  const ordersWithOldPriceList = orders
    .filter(order => order.price !== undefined)
    .filter(order => order.price.fractionalDenomination === 0)
    .map(order => order.price.fractionalDenomination);

  const sumRevenueOld = ordersWithOldPriceList.length * defaultPriceNok;

  return sumRevenueOld;
}

export function RevenueKpi({ orders }: { orders: Order[] }): JSX.Element {
  const defaultPriceNok = 149;
  const sumRevenueOld = calcRevenueOld(orders, defaultPriceNok);
  const sumRevenueNew = calcRevenueNew(orders);

  return (
    <div className="resultsPresentation">
      <div className="resultTitle"> Revenue</div>
      {sumRevenueOld && (
        <div className="textResult">
          Cost old price ({defaultPriceNok} per order) : {sumRevenueOld} NOK
        </div>
      )}
      {sumRevenueNew !== 0 && (
        <div className="textResult">Cost new price : {sumRevenueNew} NOK</div>
      )}
    </div>
  );
}

export function getNrOfDaysDelivered(orders: Order[]): number {
  const ordersToShow = nrOfOrdersByDate(orders);

  const nrOfDaysDelivered = Object.keys(ordersToShow).length;
  return nrOfDaysDelivered;
}

export function AvgDeliveriesPerDayAndPer4Hour({
  orders,
}: {
  orders: Order[];
}): JSX.Element {
  const nrOfDaysDelivered = getNrOfDaysDelivered(orders);

  const avgDeliveriesPerHourOf4HourWindow =
    orders.length / (nrOfDaysDelivered * 4);

  const avgDeliveriesPerDay = orders.length / nrOfDaysDelivered;

  return (
    <div className="resultsPresentation">
      <div className="resultTitle"> Avg time</div>
      <p>{`Average Deliveries Per Hour Of 4 HourWindow:  ${
        avgDeliveriesPerHourOf4HourWindow
          ? roundToTwo(avgDeliveriesPerHourOf4HourWindow)
          : 0
      }`}</p>

      <p>{`Average Deliveries Per Day :  ${
        avgDeliveriesPerDay ? roundToTwo(avgDeliveriesPerDay) : 0
      }`}</p>
    </div>
  );
}

export function PartnerDeviationsKpi({
  orders,
}: {
  orders: Order[];
}): JSX.Element {
  const ordersWithPartnerDeviations = orders.filter(
    order => order.deviations.length !== 0
  );

  return (
    <div className="resultsPresentation">
      <div className="resultTitle"> Nr of deviations</div>

      <div className="textResult">
        Nr of orders with Partner deviation :{' '}
        {ordersWithPartnerDeviations.length}{' '}
      </div>
    </div>
  );
}

export function getOrdersWithDelayedDeviation(orders: Order[]): Order[] {
  const ordersWithDelayedDeviation = orders.filter(order =>
    isAfterDeliverUntil(order.deliverUntil, order.deliveredAt)
  );
  return ordersWithDelayedDeviation;
}

export function getOrdersWithToEarlyDeviation(orders: Order[]): Order[] {
  const ordersWithToEarlyDeviation = orders.filter(order =>
    isBeforeDeliverFrom(order.deliverFrom, order.deliveredAt)
  );
  return ordersWithToEarlyDeviation;
}

export function AvgTimeDeviations({
  orders,
}: {
  orders: Order[];
}): JSX.Element {
  const ordersWithDelayedDeviation = getOrdersWithDelayedDeviation(orders);

  const ordersWithToEarlyDeviation = getOrdersWithToEarlyDeviation(orders);

  return (
    <div className="resultsPresentation">
      <div className="resultTitle"> Nr of delays</div>

      <div className="textResult">
        Nr of orders delivered after window :{ordersWithDelayedDeviation.length}
      </div>
      <div className="textResult">
        Procentage of orders delayed :{' '}
        {roundToTwo((100 * ordersWithDelayedDeviation.length) / orders.length)}{' '}
        %
      </div>
      <div className="textResult">
        Nr of orders delivered before window :
        {ordersWithToEarlyDeviation.length}
      </div>
      <div className="textResult">
        Procentage of orders delivered to early :{' '}
        {roundToTwo((100 * ordersWithToEarlyDeviation.length) / orders.length)}{' '}
        %
      </div>
      <div className="textResult">
        Total time deviation :{' '}
        {roundToTwo(
          (100 *
            (ordersWithDelayedDeviation.length +
              ordersWithToEarlyDeviation.length)) /
            orders.length
        )}{' '}
        %
      </div>
    </div>
  );
}
